import BaseApi, { Response, PagedResponse } from './baseApi';
import {
  BasePerson,
  CreatePerson,
  UpdatePerson,
} from '../dtos/Persons/index';

class PersonApi extends BaseApi {
  private readonly basePath = '/persons';

  /**
   * Get person by ID
   * GET /api/persons/{id}
   */
  async getById(id: string): Promise<Response<BasePerson | null>> {
    return this.get<BasePerson | null>(`${this.basePath}/${id}`);
  }

  /**
   * Get all persons
   * GET /api/persons
   */
  async getList(): Promise<Response<BasePerson[]>> {
    return this.get<BasePerson[]>(this.basePath);
  }

  /**
   * Get paginated list of persons with optional search
   * GET /api/persons/search?searchTerm=&pageNumber=1&pageSize=10 (when search is provided)
   * GET /api/persons/paged?pageNumber=1&pageSize=10 (when no search)
   * Searches: PersonInternalId, FirstName, LastName, Email, Phone, Cnp, Gender, Country, City, Address
   */
  async getPagedList(
    pageNumber: number = 1,
    pageSize: number = 10,
    searchTerm?: string
  ): Promise<Response<PagedResponse<BasePerson>>> {
    if (searchTerm) {
      const params = new URLSearchParams();
      params.append('searchTerm', searchTerm);
      params.append('pageNumber', pageNumber.toString());
      params.append('pageSize', pageSize.toString());
      return this.get<PagedResponse<BasePerson>>(
        `${this.basePath}/search?${params.toString()}`
      );
    }
    
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
    });
    return this.get<PagedResponse<BasePerson>>(
      `${this.basePath}/paged?${params.toString()}`
    );
  }

  /**
   * Create a new person
   * POST /api/persons
   */
  async create(data: CreatePerson): Promise<Response<BasePerson>> {
    return this.post<BasePerson, CreatePerson>(this.basePath, data);
  }

  /**
   * Update an existing person
   * PUT /api/persons/{id}
   */
  async update(id: string, data: Omit<UpdatePerson, 'id'>): Promise<Response<BasePerson>> {
    return this.put<BasePerson, UpdatePerson>(`${this.basePath}/${id}`, { ...data, id });
  }

  /**
   * Delete a person
   * DELETE /api/persons/{id}
   */
  async deletePerson(id: string): Promise<Response<boolean>> {
    return super.delete<boolean>(`${this.basePath}/${id}`);
  }
}

// Export singleton instance
export const personApi = new PersonApi();

export default PersonApi;
